import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { useToast } from '../../contexts/ToastContext';
import { ChevronLeft, Save, DollarSign, Truck as TruckIcon, MapPin, Calendar } from 'lucide-react';

interface EditDispatchPageProps {
  dispatchId: string;
  onBack?: () => void;
}

interface Driver {
  id: string;
  first_name: string;
  last_name: string;
  driver_type: 'company_driver' | 'owner_operator' | 'rent' | 'buy';
}

interface Truck {
  id: string;
  unit_number: string;
  driver_id: string | null;
  driver?: Driver;
}

export function EditDispatchPage({ dispatchId, onBack }: EditDispatchPageProps) {
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dispatchNumber, setDispatchNumber] = useState('');
  const [loadNumber, setLoadNumber] = useState('');
  const [totalRate, setTotalRate] = useState(0);
  const [selectedDriver, setSelectedDriver] = useState<string>('');
  const [driverPay, setDriverPay] = useState<number>(0);
  const [emptyMiles, setEmptyMiles] = useState<number>(0);
  const [driverType, setDriverType] = useState<string>('');
  const [availableTrucks, setAvailableTrucks] = useState<Truck[]>([]);
  const [selectedTruck, setSelectedTruck] = useState<string>('');
  const [pickupCity, setPickupCity] = useState<string>('');
  const [pickupState, setPickupState] = useState<string>('');
  const [pickupDate, setPickupDate] = useState<string>('');
  const [deliveryCity, setDeliveryCity] = useState<string>('');
  const [deliveryState, setDeliveryState] = useState<string>('');
  const [deliveryDate, setDeliveryDate] = useState<string>('');
  const [createdBy, setCreatedBy] = useState<string>('');
  const [totalMiles, setTotalMiles] = useState<number>(0);

  useEffect(() => {
    loadData();
  }, [dispatchId]);

  async function loadData() {
    // Load trucks first, then dispatch data
    await loadAvailableTrucks();
    await loadDispatchData();
  }

  async function loadDispatchData() {
    try {
      setLoading(true);

      const { data, error } = await supabase
        .from('dispatches')
        .select(`
          id,
          dispatch_number,
          driver_id,
          truck_id,
          driver_pay,
          empty_miles,
          created_by,
          loads(
            load_number, 
            total_rate,
            total_miles,
            pickup_city,
            pickup_state,
            pickup_date,
            delivery_city,
            delivery_state,
            delivery_date
          ),
          drivers(id, first_name, last_name, driver_type),
          trucks(id, unit_number)
        `)
        .eq('id', dispatchId)
        .single();

      if (error) throw error;

      if (data) {
        const load = Array.isArray(data.loads) ? data.loads[0] : data.loads;
        const driver = Array.isArray(data.drivers) ? data.drivers[0] : data.drivers;

        console.log('Dispatch data:', data);
        console.log('Selected truck_id:', data.truck_id);
        console.log('Selected driver_id:', data.driver_id);
        console.log('Driver from dispatch:', driver);

        // Fetch dispatcher name from profiles table
        if (data.created_by) {
          const { data: profileData } = await supabase
            .from('profiles')
            .select('full_name')
            .eq('id', data.created_by)
            .single();
          
          if (profileData?.full_name) {
            setCreatedBy(profileData.full_name);
          } else {
            setCreatedBy('System');
          }
        }

        setDispatchNumber(data.dispatch_number);
        setLoadNumber(load?.load_number || '');
        setTotalRate(load?.total_rate || 0);
        setTotalMiles(load?.total_miles || 0);
        setSelectedDriver(data.driver_id);
        setSelectedTruck(data.truck_id);
        setDriverPay(data.driver_pay || 0);
        setEmptyMiles(data.empty_miles || 0);
        setDriverType(driver?.driver_type || '');
        setPickupCity(load?.pickup_city || '');
        setPickupState(load?.pickup_state || '');
        setPickupDate(load?.pickup_date || '');
        setDeliveryCity(load?.delivery_city || '');
        setDeliveryState(load?.delivery_state || '');
        setDeliveryDate(load?.delivery_date || '');
      }
    } catch (error) {
      console.error('Error loading dispatch:', error);
      toast.error('Failed to load dispatch data');
    } finally {
      setLoading(false);
    }
  }

  async function loadAvailableTrucks() {
    try {
      const { data: trucksData, error: trucksError } = await supabase
        .from('equipment_assignments')
        .select(`
          truck_id,
          trucks!inner(id, unit_number, make, model),
          driver_id,
          drivers!inner(id, first_name, last_name, driver_type)
        `)
        .eq('is_current', true);

      if (trucksError) throw trucksError;

      console.log('Available trucks raw data:', trucksData);

      if (trucksData) {
        const trucksWithDrivers = trucksData
          .filter((a: any) => a.trucks && a.drivers)
          .map((a: any) => ({
            id: a.trucks.id,
            unit_number: a.trucks.unit_number,
            make: a.trucks.make,
            model: a.trucks.model,
            driver_id: a.driver_id,
            driver: {
              id: a.drivers.id,
              first_name: a.drivers.first_name,
              last_name: a.drivers.last_name,
              driver_type: a.drivers.driver_type,
            },
          }))
          .sort((a: any, b: any) => a.unit_number.localeCompare(b.unit_number));

        console.log('Mapped trucks with drivers:', trucksWithDrivers);
        setAvailableTrucks(trucksWithDrivers);
      }
    } catch (error) {
      console.error('Error loading trucks:', error);
    }
  }

  async function handleSave() {
    try {
      setSaving(true);

      // Validate driver pay for owner operators
      if (driverType !== 'company_driver' && (!driverPay || driverPay <= 0)) {
        toast.error('Please enter driver pay amount for owner operator');
        return;
      }

      // Validate driver pay does not exceed load rate
      if (driverPay > totalRate) {
        toast.error(`Driver pay ($${driverPay.toFixed(2)}) cannot exceed load rate ($${totalRate.toFixed(2)})`);
        return;
      }

      const { error } = await supabase
        .from('dispatches')
        .update({
          driver_id: selectedDriver,
          truck_id: selectedTruck,
          driver_pay: driverPay,
          empty_miles: emptyMiles,
        })
        .eq('id', dispatchId);

      if (error) throw error;

      toast.success('Dispatch updated successfully');
      
      if (onBack) {
        onBack();
      } else {
        window.history.back();
      }
    } catch (error) {
      console.error('Error updating dispatch:', error);
      toast.error('Failed to update dispatch');
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const selectedTruckData = availableTrucks.find(t => t.id === selectedTruck);

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <button
            onClick={() => onBack ? onBack() : window.history.back()}
            className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Edit Dispatch</h1>
            <p className="text-sm text-slate-600 mt-1">
              {dispatchNumber} • Load #{loadNumber}
            </p>
            {createdBy && (
              <p className="text-xs text-slate-500 mt-1">
                Dispatcher: {createdBy}
              </p>
            )}
          </div>
        </div>

        {/* Load Info */}
        <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="grid grid-cols-6 gap-4">
            <div>
              <p className="text-sm text-slate-600">Load Rate</p>
              <p className="text-xl font-bold text-green-600">${totalRate.toLocaleString('en-US', { minimumFractionDigits: 2 })}</p>
            </div>
            <div>
              <p className="text-sm text-slate-600">Loaded Miles</p>
              <p className="text-xl font-bold text-slate-900">{totalMiles.toLocaleString()}</p>
            </div>
            <div>
              <p className="text-sm text-slate-600">RPM</p>
              <p className="text-xl font-bold text-blue-600">${totalMiles > 0 ? (totalRate / totalMiles).toFixed(2) : '0.00'}</p>
            </div>
            {driverType !== 'company_driver' && driverPay > 0 && driverPay <= totalRate && (
              <>
                <div>
                  <p className="text-sm text-slate-600">Driver Gets</p>
                  <p className="text-xl font-bold text-purple-600">
                    ${driverPay.toFixed(2)}
                  </p>
                  <p className="text-xs text-slate-500">
                    {totalRate > 0 ? `${((driverPay / totalRate) * 100).toFixed(1)}%` : '0%'}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-slate-600">Dispatcher Cut</p>
                  <p className="text-xl font-bold text-orange-600">
                    ${(totalRate - driverPay).toFixed(2)}
                  </p>
                  <p className="text-xs text-slate-500">
                    {totalRate > 0 ? `${(((totalRate - driverPay) / totalRate) * 100).toFixed(1)}%` : '0%'}
                  </p>
                </div>
              </>
            )}
            <div className="text-right">
              <p className="text-sm text-slate-600">Load Number</p>
              <p className="text-lg font-semibold text-slate-900">#{loadNumber}</p>
            </div>
          </div>
        </div>

        {/* Load Details - Pickup & Delivery */}
        <div className="mb-6 p-4 bg-slate-50 border border-slate-200 rounded-lg">
          <div className="grid grid-cols-3 gap-4">
            {/* Pickup */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                <MapPin className="w-4 h-4 text-green-600" />
                <p className="text-sm font-semibold text-slate-700">Pickup</p>
              </div>
              <p className="text-base font-semibold text-slate-900">
                {pickupCity && pickupState ? `${pickupCity}, ${pickupState}` : 'Not specified'}
              </p>
              {pickupDate && (
                <div className="flex items-center gap-1 mt-1">
                  <Calendar className="w-3 h-3 text-slate-500" />
                  <p className="text-sm text-slate-600">
                    {new Date(pickupDate).toLocaleDateString('en-US', { 
                      month: 'short', 
                      day: 'numeric', 
                      year: 'numeric' 
                    })}
                  </p>
                </div>
              )}
            </div>

            {/* Delivery */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                <MapPin className="w-4 h-4 text-red-600" />
                <p className="text-sm font-semibold text-slate-700">Delivery</p>
              </div>
              <p className="text-base font-semibold text-slate-900">
                {deliveryCity && deliveryState ? `${deliveryCity}, ${deliveryState}` : 'Not specified'}
              </p>
              {deliveryDate && (
                <div className="flex items-center gap-1 mt-1">
                  <Calendar className="w-3 h-3 text-slate-500" />
                  <p className="text-sm text-slate-600">
                    {new Date(deliveryDate).toLocaleDateString('en-US', { 
                      month: 'short', 
                      day: 'numeric', 
                      year: 'numeric' 
                    })}
                  </p>
                </div>
              )}
            </div>

            {/* Empty Miles */}
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">
                Empty Miles
              </label>
              <input
                type="number"
                step="1"
                value={emptyMiles}
                onChange={(e) => setEmptyMiles(Number(e.target.value))}
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="0"
                min="0"
              />
            </div>
          </div>
        </div>

        {/* Current Assignment */}
        <div className="mb-6 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
              <TruckIcon className="w-5 h-5 text-blue-600" />
              Current Assignment
            </h3>
          </div>
          {selectedTruckData ? (
            <div className="grid grid-cols-3 gap-4">
              <div>
                <p className="text-sm text-slate-600">Truck Number</p>
                <p className="text-xl font-bold text-slate-900">{selectedTruckData.unit_number}</p>
              </div>
              <div>
                <p className="text-sm text-slate-600">Driver</p>
                <p className="text-xl font-bold text-slate-900">
                  {selectedTruckData.driver ? `${selectedTruckData.driver.first_name} ${selectedTruckData.driver.last_name}` : 'No Driver'}
                </p>
              </div>
              <div>
                <p className="text-sm text-slate-600">Driver Type</p>
                <p className="text-xl font-bold text-blue-600 capitalize">
                  {selectedTruckData.driver ? selectedTruckData.driver.driver_type.replace('_', ' ') : '-'}
                </p>
              </div>
            </div>
          ) : (
            <p className="text-slate-600">No truck assigned</p>
          )}
        </div>

        {/* Form */}
        <div className="space-y-6">
          {/* Driver Pay */}
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">
              <DollarSign className="w-4 h-4 inline mr-2" />
              Driver Pay
              <span className="text-slate-500 ml-2 font-normal">
                (Load Rate: ${totalRate.toFixed(2)})
              </span>
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-600 font-semibold">$</span>
              <input
                type="number"
                step="0.01"
                value={driverPay}
                onChange={(e) => setDriverPay(Number(e.target.value))}
                className={`w-full pl-8 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                  driverType === 'company_driver' ? 'bg-blue-50 border-blue-300' : 
                  driverPay > totalRate ? 'border-red-500' : 'border-slate-300'
                }`}
                placeholder="Enter driver pay amount"
                min="0"
                readOnly={driverType === 'company_driver'}
              />
            </div>

            {driverPay > totalRate && (
              <p className="mt-2 text-sm text-red-600">
                ⚠️ Driver pay (${driverPay.toFixed(2)}) exceeds load rate (${totalRate.toFixed(2)}). This cannot be saved.
              </p>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4 border-t border-slate-200">
            <button
              type="button"
              onClick={() => onBack ? onBack() : window.history.back()}
              className="flex-1 px-6 py-3 border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors font-semibold"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving || driverPay > totalRate || (driverType !== 'company_driver' && driverPay <= 0)}
              className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              <Save className="w-4 h-4" />
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
